import * as React from "react";
import { styled, css } from "styled-components";
import { Image } from "@ludens-reklame/rubics-v3-sdk/file/types";
import { transformImageUrl } from "@ludens-reklame/rubics-v3-sdk/file/utils";
import { mobileBreakPointCSS } from "../../defaults/theme.js";

import {
  Constrainer,
  HorizontalSpacer,
  VerticalSpacer,
} from "../../internal-components/Layout.js";

export type Layout = "a" | "b" | "c";

export interface ImageProp {
  image?: Image;
}

export interface Props {
  layout?: Layout;
  images?: ImageProp[];
}

export const ImageGrid: React.FunctionComponent<Props> = ({
  layout = "a",
  images = [],
}) => {
  if (images.length === 0) {
    return null;
  }

  return (
    <VerticalSpacer>
      <HorizontalSpacer>
        <Constrainer
          $width={layout === "b" || layout === "c" ? "medium" : "small"}
        >
          <Grid $layout={layout}>
            {images.map((i, k) => {
              if (!i.image) {
                return null;
              }

              return (
                <figure key={k}>
                  <img
                    src={transformImageUrl(i.image, {
                      width: 1920,
                      height: 1080,
                      fit: "cover",
                    })}
                    alt={i.image.altText || ""}
                  />
                  {i.image.caption && (
                    <figcaption>{i.image.caption}</figcaption>
                  )}
                </figure>
              );
            })}
          </Grid>
        </Constrainer>
      </HorizontalSpacer>
    </VerticalSpacer>
  );
};

export const Grid = styled.div<{ $layout: Layout }>`
  display: grid;
  grid-gap: ${(props) => props.theme.spacing.s};

  * {
    display: block;
    width: 100%;
  }

  figure {
    margin: 0;
  }

  img {
    border-radius: ${(props) => props.theme.borderRadius};
  }

  figcaption {
    padding-top: ${(props) => props.theme.spacing.xs};
    width: auto;
    font-style: italic;
  }

  @media (min-width: ${mobileBreakPointCSS}) {
    grid-gap: ${(props) => props.theme.spacing.l};
  }

  ${(props) =>
    props.$layout === "a" &&
    css`
      figure:nth-child(even) img {
        border-radius: ${props.theme.borderRadius};
      }
    `}

  ${(props) =>
    props.$layout === "b" &&
    css`
      grid-template-columns: 1fr 1fr;

      figure:nth-child(3n) {
        grid-column: span 2;
      }
    `}

  ${(props) =>
    props.$layout === "c" &&
    css`
      display: flex;
      flex-wrap: wrap;
      align-items: flex-end;

      figure {
        width: calc(33.332% - ${props.theme.spacing.s} / 2);
      }

      img {
        border-radius: ${props.theme.borderRadius};
      }

      figure:nth-child(3n-1) {
        width: calc(66.662% - ${props.theme.spacing.s} / 2);
      }

      figure:nth-child(3n) {
        width: 66.662%;
        margin-left: auto;
        margin-right: auto;
      }

      @media (min-width: ${mobileBreakPointCSS}) {
        figure {
          width: calc(33.332% - ${props.theme.spacing.l} / 2);
        }

        figure:nth-child(3n-1) {
          width: calc(66.662% - ${props.theme.spacing.l} / 2);
        }
      }
    `}

`;
